import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import { gql, Resolver } from '@apollo/client';

/**
 * See docs: https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ForgotPassword.html
 * @typedef {ForgotPasswordArgs}
 * @property {string} clientId
 * @property {string} username
 * @property {string} userPoolId
 */
interface ForgotPasswordArgs {
    clientId: string;
    username: string;
    userPoolId: string;
}

/**
 * Call the `cognitoUser.forgotPassword()` method to initiate a password reset. This is step 1 of a
 * 2-step process; the second step is `cognitoUser.confirmPassword()`, which is called in
 * confirmPassword.ts.
 * @param obj
 * @param {ForgotPasswordArgs} args
 */
export const forgotPassword: Resolver = async (
    obj: Record<string, unknown>,
    args: ForgotPasswordArgs,
): Promise<CognitoActionResult> => {
    const { clientId, username, userPoolId } = args;
    const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
    });
    const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
    });

    const requestPasswordReset = (): Promise<CognitoActionResult> =>
        new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess: (): void => {
                    resolve({ success: true });
                },
                onFailure: (error): void => {
                    reject(new Error(error.message || JSON.stringify(error)));
                },
            });
        });

    return requestPasswordReset();
};

export const FORGOT_PASSWORD = gql`
    mutation ForgotPassword(
        $clientId: String!
        $username: String!
        $userPoolId: String!
    ) {
        forgotPassword(
            clientId: $clientId
            username: $username
            userPoolId: $userPoolId
        ) @client
    }
`;
